import { Address } from '@lockton-apps/shared/interfaces';
import {
  compact,
  find,
  flow,
  get,
  isObject,
  join,
  replace,
  toString,
} from 'lodash';
import { Observable } from 'rxjs';
import { FormlyFieldConfigWithDisplayViewOptions } from '../interfaces';

export { DynamicFormsPresentationValueAccessor } from './dynamic-forms-value-accessor.service';
export { PresentationBuilderValueAccessor } from './presentation-builder-value-accessor.service';

interface LabelValue {
  label: string;
  value: any;
  rawValue: unknown;
  field: FormlyFieldConfigWithDisplayViewOptions;
}

type DynamicFormValueAccessor = (
  field: FormlyFieldConfigWithDisplayViewOptions,
  model: unknown
) => LabelValue;

const baseAccessor: DynamicFormValueAccessor = (field, value) => {
  const label = get(
    field,
    'displayViewOptions.props.label',
    get(field, 'props.longLabel', get(field, 'props.label', ''))
  );
  return { label, value, rawValue: value, field };
};

const valueToString = (data: LabelValue) => {
  return { ...data, value: toString(data.value) };
};

const addonLeft = (data: LabelValue) => {
  const prefix = get(data.field, 'props.addonLeft');
  return {
    ...data,
    value: join(compact([prefix, data.value]), ' '),
  };
};

const addonRight = (data: LabelValue) => {
  const suffix = get(data.field, 'props.addonRight');
  return {
    ...data,
    value: join(compact([data.value, suffix]), ' '),
  };
};

const defaultAccessor: DynamicFormValueAccessor = (field, value) =>
  flow(baseAccessor, valueToString, addonLeft, addonRight)(field, value);

const toCurrency = (data: LabelValue) => {
  const regex = /\d{1,3}(?=(\d{3})+(?!\d))/g;
  const commaSeperated = replace(data.value, regex, '$&,');
  return {
    ...data,
    value: `${commaSeperated}`,
  };
};

const formatDate = (data: LabelValue) => {
  const date = data.value
    ? new Date(data.value).toLocaleDateString()
    : undefined;
  return {
    ...data,
    value: date,
  };
};

const mapOptionLabel = (data: LabelValue) => {
  const options = get(
    data.field,
    'props.options',
    [] as { label: string; value: unknown }[]
  );

  if (options instanceof Observable) {
    return { ...data, value: toString(data.value) };
  }
  const selectedOption = find(options, (v) => v.value === data.value);
  const valueLabel = toString(get(selectedOption, 'label', data.value));
  return { ...data, value: valueLabel };
};

const transformAddress = (data: LabelValue) => {
  if (!isAddress(data.value)) {
    return { ...data, value: '-' };
  }
  const value = data.value as Address;
  const transformedValue = value
    ? join(
        compact([value.street, value.cityTown, value.country, value.postcode]),
        ', '
      )
    : '-';
  return { ...data, value: transformedValue };
};

const getAddress = (data: LabelValue) => {
  const address = get(data.value, 'address', {});
  return { ...data, value: address };
};

export const valueAccessors: Record<string, DynamicFormValueAccessor> = {
  string: defaultAccessor,
  input: defaultAccessor,
  textarea: defaultAccessor,
  number: defaultAccessor,
  datepicker: flow(baseAccessor, formatDate),
  currency: flow(baseAccessor, toCurrency, addonLeft, addonRight),
  summary: flow(baseAccessor, toCurrency, addonLeft, addonRight),
  select: flow(baseAccessor, mapOptionLabel),
  'select-box': flow(baseAccessor, mapOptionLabel),
  address: flow(baseAccessor, transformAddress),
  addressSpecial: flow(baseAccessor, getAddress, transformAddress),
  'image-file-input': baseAccessor,
};

function isAddress(value: unknown): value is Address {
  return isObject(value) && 'street' in value;
}
